/**
 * Draining the outbox, as decisions.
 *
 * WHY THIS FILE EXISTS AT ALL, and it is the reason `sync-failure.ts` and
 * `team-data.ts` exist beside their callers: `sync-engine.ts` reaches
 * `expo-sqlite` through the repository and `expo/fetch` through the API, so
 * bun cannot load it and nothing in it can be tested. The drain is the part
 * worth proving — it decides which writes are sent together, which are sent
 * again alone, and which are thrown away forever — so it lives here, where a
 * test can hold it, and the engine keeps only the wiring.
 *
 * Two rules, and the second exists because of the first.
 *
 *   1. Consecutive creates of ONE record go in ONE batch, up to
 *      `ENTRY_BATCH_MAX`. A sheet of attendance marks is fifty rows saved at
 *      once, and the batch route writes them in one transaction (Decision 10
 *      of docs/plans/done/entry-sheet.md).
 *   2. A 4xx from a batch sends that group again ONE AT A TIME. The batch is
 *      all or nothing, so a 4xx means nothing landed and one row of the group
 *      is bad. Dropping the whole group would delete rows the server never
 *      refused; replaying it singly lets the per-op rule find exactly the bad
 *      one and drop only that. A 5xx or a dead network still stops the drain,
 *      because a refusal is final and a failure is not.
 *   3. An `edit` goes ALONE, in the place it was made. It is a PATCH and a
 *      create is a POST, so there is no call that could carry both; and its
 *      place matters more than its company, because a mark corrected after it
 *      was written is only a correction if it arrives second (Decision 11).
 *      A refused edit drops its own op and the drain carries on — unlike a
 *      refused create, the row it names is one the server HAS.
 */
import { ENTRY_BATCH_MAX, type EntryLinkOutcome, type EntryWriteResponse } from '@alluzo/shared';

import type { OutboxOp } from '@/lib/entry-repo';
import { mayDropOp } from '@/lib/sync-failure';

/** The side effects a drain has, handed in so this file can be watched. */
export type DrainCalls = {
  /** One create, exactly as the push has always sent it. */
  readonly one: (op: OutboxOp) => Promise<EntryWriteResponse>;
  /** A group of creates of one record, in order, in one transaction. */
  readonly many: (recordId: string, ops: readonly OutboxOp[]) => Promise<EntryWriteResponse[]>;
  /** One correction to a row the server already holds. */
  readonly edit: (op: OutboxOp) => Promise<EntryWriteResponse>;
  readonly markSynced: (
    entryId: string,
    ack: { version: number; versionId: string; serverSeq: number },
  ) => void;
  /**
   * ONE op of an entry acked, not every op of it. An entry can owe two edits,
   * and the first ack must not retire the second.
   */
  readonly markSyncedOp: (
    opId: string,
    entryId: string,
    ack: { version: number; versionId: string; serverSeq: number },
  ) => void;
  readonly removePending: (entryId: string) => void;
  /** A refused edit: the op goes, the row stays, the next pull corrects it. */
  readonly dropEditOp: (opId: string, entryId: string) => void;
  /**
   * SAY SO. A refused write is final, and somebody typed it. The engine turns
   * this into the notice that survives every healthy run after it.
   */
  readonly noteRefusal: (op: OutboxOp, thrown: unknown) => void;
  /**
   * What an acked write's links did: the rows they posted, and the ones that
   * held but could not write (L3 of docs/plans/links-fixes.md). Called only
   * when the answer carries it, after the op is marked synced. The engine
   * turns it into the save's "Added to Till", and a refusal into a
   * notice that stays until read.
   */
  readonly noteLinks: (op: OutboxOp, outcome: EntryLinkOutcome) => void;
};

/** One thing the drain will do: a batch of creates, or a single edit. */
export type DrainStep =
  | { readonly kind: 'creates'; readonly ops: readonly OutboxOp[] }
  | { readonly kind: 'edit'; readonly op: OutboxOp };

/**
 * The ops of one drain, cut into the calls they will be sent as.
 *
 * CONSECUTIVE is the whole of it. The outbox is in created_at order and that
 * order is the person's own — a mark, then a sale, then another mark — so a run
 * is broken by a different record and by an op of another kind, never
 * reordered to make a bigger batch. A batch that reorders writes is a batch
 * that can put a correction before the thing it corrects.
 *
 * An `edit` is a step of its own and it ENDS the run it lands in. It has to:
 * PATCH is another route, and a mark corrected after it was written only reads
 * as a correction if it arrives after the write.
 */
export const cutOutbox = (ops: readonly OutboxOp[]): readonly DrainStep[] => {
  const steps: DrainStep[] = [];
  let run: OutboxOp[] | null = null;
  for (const op of ops) {
    if (op.kind !== 'create') {
      run = null;
      // Only `edit` has a call. An op of a kind this build does not know is
      // left where it is rather than guessed at — a newer binary wrote it and
      // an older one has no business sending it anywhere.
      if (op.kind === 'edit') steps.push({ kind: 'edit', op });
      continue;
    }
    if (run !== null && run[0]?.recordId === op.recordId && run.length < ENTRY_BATCH_MAX) {
      run.push(op);
      continue;
    }
    run = [op];
    steps.push({ kind: 'creates', ops: run });
  }
  return steps;
};

/**
 * Which creates go in one call, for a reader that only cares about batching.
 *
 * Derived from `cutOutbox` rather than written twice, so the runs a test
 * describes here are the runs the drain actually sends.
 */
export const groupCreates = (ops: readonly OutboxOp[]): readonly (readonly OutboxOp[])[] =>
  cutOutbox(ops).flatMap((step) => (step.kind === 'creates' ? [step.ops] : []));

/**
 * Send the outbox, in order, and answer the records something landed in.
 *
 * The drain STOPS at the first failure that is not a refusal — the remaining
 * ops keep their place for the next trigger — and carries on past every refusal
 * it drops, because a 4xx is final for that op and says nothing about the next
 * one.
 */
export const drainOutbox = async (
  ops: readonly OutboxOp[],
  calls: DrainCalls,
): Promise<readonly string[]> => {
  const touched = new Set<string>();

  /** One op the old way. False means stop the drain. */
  const single = async (op: OutboxOp): Promise<boolean> => {
    try {
      const written = await calls.one(op);
      calls.markSynced(op.entryId, {
        version: written.version,
        versionId: written.versionId,
        serverSeq: written.serverSeq,
      });
      if (written.linkFirings !== undefined) calls.noteLinks(op, written.linkFirings);
      touched.add(op.recordId);
      return true;
    } catch (thrown) {
      // Only a 4xx drops the op; `mayDropOp` holds that rule and is tested
      // there. A 5xx or a transport fault breaks the drain instead, so the
      // whole outbox waits for the next trigger. Dropping on a 503 would
      // delete an entry the server never refused.
      if (!mayDropOp(thrown)) return false;
      calls.noteRefusal(op, thrown);
      calls.removePending(op.entryId);
      touched.add(op.recordId);
      return true;
    }
  };

  /** One edit, alone. False means stop the drain. */
  const sendEdit = async (op: OutboxOp): Promise<boolean> => {
    try {
      const written = await calls.edit(op);
      // By OP, not by entry: two edits can be queued on one row and the second
      // is still owed after the first is answered.
      calls.markSyncedOp(op.opId, op.entryId, {
        version: written.version,
        versionId: written.versionId,
        serverSeq: written.serverSeq,
      });
      if (written.linkFirings !== undefined) calls.noteLinks(op, written.linkFirings);
      touched.add(op.recordId);
      return true;
    } catch (thrown) {
      if (!mayDropOp(thrown)) return false;
      calls.noteRefusal(op, thrown);
      // The op goes and the ROW STAYS. A refused create is a ghost and is
      // deleted; a refused edit names an entry the server has, and deleting
      // that would throw away a write it took because a correction to it was
      // refused. The next pull is what puts the row right.
      calls.dropEditOp(op.opId, op.entryId);
      touched.add(op.recordId);
      return true;
    }
  };

  for (const step of cutOutbox(ops)) {
    if (step.kind === 'edit') {
      if (!(await sendEdit(step.op))) return [...touched];
      continue;
    }

    const group = step.ops;
    const first = group[0];
    if (first === undefined) continue;
    if (group.length === 1) {
      if (!(await single(first))) return [...touched];
      continue;
    }

    try {
      const written = await calls.many(first.recordId, group);
      const acks = new Map(written.map((ack) => [ack.entryId, ack]));
      for (const op of group) {
        const ack = acks.get(op.entryId);
        // A receipt the batch did not answer for is neither a success nor a
        // refusal: it is an answer this app cannot read. Stop, and leave the
        // op where it is. Marking it synced would lose a write the server may
        // never have taken; dropping it would delete one it may have.
        if (ack === undefined) return [...touched];
        calls.markSynced(op.entryId, {
          version: ack.version,
          versionId: ack.versionId,
          serverSeq: ack.serverSeq,
        });
        if (ack.linkFirings !== undefined) calls.noteLinks(op, ack.linkFirings);
        touched.add(op.recordId);
      }
    } catch (thrown) {
      if (!mayDropOp(thrown)) return [...touched];
      // Rule 2: the batch rolled back, so every op of it is unsent. Send them
      // one at a time and let the per-op rule drop exactly the bad one.
      for (const op of group) {
        if (!(await single(op))) return [...touched];
      }
    }
  }

  return [...touched];
};
